import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const RESET_INTERVAL = 120; // 2 minutes in seconds

const formatCountdown = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

// Formats a past timestamp (ms) as a short "time ago" string, e.g. "just now",
// "5 min ago", "1 hr ago". Returns null if no timestamp is available.
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return null;
  const diffMs = Date.now() - timestamp;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin === 1) return "1 min ago";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  return diffHr === 1 ? "1 hr ago" : `${diffHr} hrs ago`;
};

const DEMO_QUEUE = [
  { number: 1, name: "John Smith", checkedInAt: Date.now() - 8 * 60000 },
  { number: 2, name: "Jane Doe", checkedInAt: Date.now() - 4 * 60000 },
  { number: 3, name: "Michael Lee", checkedInAt: Date.now() - 60000 },
];

const DEMO_TASKS = [
  { number: 1, name: "Reply to client emails", notes: "3 urgent emails waiting", priority: "high" },
  { number: 2, name: "Pay monthly invoices", notes: "Due by end of day", priority: "high" },
  { number: 3, name: "Team meeting prep", notes: "Prepare agenda for 2pm", priority: "medium" },
  { number: 4, name: "Restock office supplies", notes: "Printer paper & coffee", priority: "medium" },
  { number: 5, name: "Update social media", notes: null, priority: "low" },
  { number: 6, name: "Clean & tidy front desk", notes: null, priority: "low" },
];

const DemoQueueManager = () => {
  const navigate = useNavigate();

  const [queue, setQueue] = useState(DEMO_QUEUE);
  const [counter, setCounter] = useState(7);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [newNumber, setNewNumber] = useState(null);
  const [countdown, setCountdown] = useState(RESET_INTERVAL);
  const [notes, setNotes] = useState("");
  const [notesLabel, setNotesLabel] = useState("Notes");
  const [editingLabel, setEditingLabel] = useState(false);
  const [activeTab, setActiveTab] = useState("checkin");
  const [servingNums, setServingNums] = useState([]);
  const [mode, setMode] = useState("customer"); // "customer" | "task"
  const [priority, setPriority] = useState("medium");
  const modeRef = useRef("customer");
  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Ticks every 30s purely to force a re-render so the "X min ago" labels stay fresh.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setNowTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // Auto-reset check-in panel after 10 s
  useEffect(() => {
    if (!submitted) return;
    const t = setTimeout(resetForm, 10000);
    return () => clearTimeout(t);
  }, [submitted]);

  // Auto-switch back to Check In after 15 s of inactivity on Queue tab
  // (only in customer mode — task mode users manage intentionally)
  useEffect(() => {
    if (activeTab !== "queue" || mode !== "customer") return;
    let timer = setTimeout(() => setActiveTab("checkin"), 15000);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setActiveTab("checkin"), 15000);
    };
    window.addEventListener("mousemove", reset);
    window.addEventListener("touchstart", reset);
    window.addEventListener("click", reset);
    window.addEventListener("keydown", reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", reset);
      window.removeEventListener("touchstart", reset);
      window.removeEventListener("click", reset);
      window.removeEventListener("keydown", reset);
    };
  }, [activeTab, mode]);

  // 2-minute auto-clear: reset queue, counter and form
  useEffect(() => {
    const tick = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setQueue(modeRef.current === "task" ? DEMO_TASKS : DEMO_QUEUE);
          setCounter(7);
          setName("");
          setPhone("");
          setNotes("");
          setNewNumber(null);
          setSubmitted(false);
          setServingNums([]);
          return RESET_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const handleJoin = (e) => {
    e.preventDefault();
    const number = counter;
    setCounter((c) => c + 1);
    setQueue((q) => {
      const updated = [...q, { number, name, phone: phone || null, notes: notes || null, priority: mode === "task" ? priority : null, checkedInAt: Date.now() }];
      // Customers see their position in the current line (resets as people are served),
      // not the ever-incrementing internal ticket number.
      setNewNumber(mode === "customer" ? updated.length : number);
      return updated;
    });
    setSubmitted(true);
  };

  const resetForm = () => {
    setName("");
    setPhone("");
    setNotes("");
    setPriority("medium");
    setNewNumber(null);
    setSubmitted(false);
  };

  const serveNow = (number) =>
    setServingNums((s) => [...s, number]);

  const markServedByNumber = (number) => {
    setQueue((q) => q.filter((c) => c.number !== number));
    setServingNums((s) => s.filter((n) => n !== number));
  };

  const removeCustomer = (number) => {
    setQueue((q) => q.filter((c) => c.number !== number));
    setServingNums((s) => s.filter((n) => n !== number));
  };

  const clearAll = () => {
    if (!window.confirm("Clear the demo queue and reset the counter?")) return;
    setQueue([]);
    setServingNums([]);
    setCounter(1);
  };

  const current = queue[0];
  const serving = queue.filter((c) => servingNums.includes(c.number));
  const waiting = queue.filter((c) => !servingNums.includes(c.number));
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const sortedWaiting = mode === "task"
    ? [...waiting].sort((a, b) => (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1))
    : waiting;
  // Displayed queue position — based on current position in line, so numbers stay
  // small and re-use freed-up slots as customers are served, instead of growing forever.
  const getPosition = (cust) => queue.findIndex((c) => c.number === cust.number) + 1;
  const displayNumber = (cust) => (mode === "customer" ? getPosition(cust) : cust.number);

  const toggleMode = () => {
    const newMode = mode === "customer" ? "task" : "customer";
    setMode(newMode);
    setQueue(newMode === "task" ? DEMO_TASKS : DEMO_QUEUE);
    setCounter(7);
    setServingNums([]);
    resetForm();
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-100">

      {/* Demo banner */}
      <div className="bg-yellow-400 text-yellow-900 text-sm font-semibold py-2 px-4 flex-shrink-0 flex items-center justify-center gap-3 flex-wrap">
        <span>🎭 Demo only — this is not a real queue. Nothing is saved or sent to any server.</span>
        <span className="bg-yellow-700 text-yellow-100 rounded-full px-3 py-0.5 text-xs font-bold tabular-nums">
          Auto-clears in {formatCountdown(countdown)}
        </span>
      </div>

      {/* Header */}
      <header className="bg-white shadow-sm px-4 sm:px-6 py-3 grid grid-cols-3 items-center flex-shrink-0 z-10">
        {/* Left: business name */}
        <div className="min-w-0">
          <h1 className="text-sm sm:text-xl font-bold text-gray-800 truncate">
            Your Business Name
          </h1>
          <p className="text-xs text-gray-400">
            {queue.length} {mode === "task" ? "task" : "customer"}{queue.length !== 1 ? "s" : ""} in queue
          </p>
          <button
            onClick={toggleMode}
            className={`hidden sm:inline-flex mt-0.5 text-xs px-2 py-0.5 rounded-full font-semibold border transition items-center gap-1 ${
              mode === "customer"
                ? "bg-blue-50 text-blue-600 border-blue-300 hover:bg-blue-100"
                : "bg-purple-50 text-purple-600 border-purple-300 hover:bg-purple-100"
            }`}
          >
            {mode === "customer" ? "� Switch to Tasks Mode" : "👥 Switch to Queue Mode"}
          </button>
        </div>

        {/* Centre: branding */}
        <a
          href="https://customerqueue.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center group"
        >
          <span className="text-xs sm:text-2xl font-extrabold tracking-tight text-center bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 bg-clip-text text-transparent group-hover:from-emerald-600 group-hover:via-teal-600 group-hover:to-cyan-600 transition-all duration-300 leading-tight drop-shadow-sm">
            CustomerQueue.com
          </span>
          <span className="hidden sm:block text-[10px] font-semibold tracking-[0.2em] uppercase mt-0.5 bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent group-hover:from-teal-500 group-hover:to-cyan-500 transition-colors duration-300">
            ❆ Queue smarter ❆
          </span>
        </a>

        {/* Right: back button */}
        <div className="flex justify-end">
          <button
            onClick={() => navigate("/")}
            className="flex-shrink-0 bg-gray-100 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
          >
            ← Back
          </button>
        </div>
      </header>

      {/* Mobile tab switcher */}
      {/* Mobile-only mode toggle */}
      <div className="sm:hidden flex justify-center bg-white border-b border-gray-100 py-1.5 flex-shrink-0">
        <button
          onClick={toggleMode}
          className={`text-xs px-4 py-1 rounded-full font-semibold border transition ${
            mode === "customer"
              ? "bg-blue-50 text-blue-600 border-blue-300 hover:bg-blue-100"
              : "bg-purple-50 text-purple-600 border-purple-300 hover:bg-purple-100"
          }`}
        >
          {mode === "customer" ? "📋 Switch to Tasks Mode" : "👥 Switch to Queue Mode"}
        </button>
      </div>
      <div className="flex border-b border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={() => setActiveTab("checkin")}
          className={`flex-1 py-2.5 text-sm font-semibold transition ${
            activeTab === "checkin"
              ? "text-green-600 border-b-2 border-green-500"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          {mode === "customer" ? "👋 Check In" : "📋 Add Task"}
        </button>
        <button
          onClick={() => setActiveTab("queue")}
          className={`flex-1 py-2.5 text-sm font-semibold transition ${
            activeTab === "queue"
              ? "text-green-600 border-b-2 border-green-500"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          {mode === "customer" ? "📋 Queue" : "📊 Tasks"}{queue.length > 0 ? ` (${queue.length})` : ""}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">

        {/* LEFT PANEL: Customer Check-In */}
        <div
          className={`${activeTab === "checkin" ? "flex w-full" : "hidden"} bg-white flex-col items-center justify-start px-6 sm:px-10 py-6 sm:py-10 overflow-y-auto`}
        >
          <div className="text-center mb-4 sm:mb-8">
            {mode === "customer" && (
              <p className="text-sm font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 inline-block px-3 py-1 rounded-full mb-3">
                {waiting.length} {waiting.length === 1 ? "customer" : "customers"} currently waiting
              </p>
            )}
            <div className="text-4xl sm:text-5xl mb-2">{mode === "customer" ? "👋" : "📋"}</div>
            <h2 className="text-xl sm:text-3xl font-bold text-gray-800 mb-1">
              {mode === "customer" ? "Please check in here" : "Add a New Task"}
            </h2>
            <p className="text-sm text-gray-400">{mode === "customer" ? "Enter your name to join the queue" : "Enter task details and set its priority"}</p>
          </div>

          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                onSubmit={handleJoin}
                className="w-full max-w-sm"
              >
                <label className="block mb-4">
                  <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    {mode === "customer" ? "Name" : "Task Title"} *
                  </span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="off"
                    className="w-full mt-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                    placeholder={mode === "customer" ? "Enter your name" : "Enter task title"}
                  />
                </label>

                {mode === "task" && (
                  <div className="block mb-4">
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wide block mb-2">Priority</span>
                    <div className="flex gap-2">
                      {[
                        { value: "high", label: "🔴 High", activeClass: "bg-red-100 border-red-500 text-red-700" },
                        { value: "medium", label: "🟡 Medium", activeClass: "bg-yellow-100 border-yellow-500 text-yellow-700" },
                        { value: "low", label: "🔵 Low", activeClass: "bg-blue-100 border-blue-400 text-blue-700" },
                      ].map(({ value, label, activeClass }) => (
                        <button
                          type="button"
                          key={value}
                          onClick={() => setPriority(value)}
                          className={`flex-1 py-2 rounded-lg font-semibold text-sm border-2 transition ${
                            priority === value ? activeClass : "border-gray-200 text-gray-400 hover:border-gray-300 bg-white"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <label className="mb-6 hidden">
                  <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Phone (optional)
                  </span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full mt-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                    placeholder="Phone number"
                  />
                </label>

                <label className="block mb-6">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">
                    {editingLabel ? (
                      <input
                        type="text"
                        value={notesLabel}
                        onChange={(e) => setNotesLabel(e.target.value)}
                        onBlur={() => setEditingLabel(false)}
                        onKeyDown={(e) => { if (e.key === "Enter") setEditingLabel(false); }}
                        autoFocus
                        className="border-b border-gray-400 bg-transparent text-sm font-medium text-gray-500 uppercase tracking-wide focus:outline-none w-32"
                      />
                    ) : (
                      <>
                        {notesLabel}
                        <button
                          type="button"
                          onClick={() => setEditingLabel(true)}
                          title="Edit label"
                          className="text-gray-300 hover:text-gray-500 transition text-base leading-none"
                        >
                          ✎
                        </button>
                      </>
                    )}
                    <span className="normal-case font-normal tracking-normal text-gray-400">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full mt-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                    placeholder="Any special notes…"
                  />
                </label>

                <button
                  type="submit"
                  className="w-full bg-green-600 text-white text-xl font-bold py-4 rounded-xl hover:bg-green-700 active:scale-95 transition"
                >
                  {mode === "customer" ? "Join Queue" : "Add Task →"}
                </button>

                {mode === "customer" && waiting.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-1.5 text-center">
                      Up next
                    </p>
                    <div className="space-y-1">
                      {waiting.slice(0, 4).map((c) => (
                        <div
                          key={c.number}
                          className="text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-3 py-1.5 flex items-center justify-between"
                        >
                          <span className="font-semibold text-gray-600">#{displayNumber(c)}</span>
                          <span className="truncate ml-2 flex-1">{c.name}</span>
                          {formatTimeAgo(c.checkedInAt) && (
                            <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{formatTimeAgo(c.checkedInAt)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.form>
            ) : (
              <motion.div
                key="ticket"
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                className="text-center w-full max-w-sm"
              >
                <p className="text-gray-500 text-lg mb-1">{mode === "customer" ? "Your queue number is" : "Task added!"}</p>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  className="text-7xl sm:text-9xl font-extrabold text-green-600 leading-none mb-3"
                >
                  #{newNumber}
                </motion.div>
                <p className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">
                  {mode === "customer" ? `Thanks, ${name}!` : name}
                </p>
                {mode === "task" && (
                  <p className={`text-sm font-semibold px-3 py-1 rounded-full mb-2 inline-block ${priority === "high" ? "bg-red-100 text-red-600" : priority === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-600"}`}>
                    {priority === "high" ? "🔴 High Priority" : priority === "medium" ? "🟡 Medium Priority" : "🔵 Low Priority"}
                  </p>
                )}
                <p className="text-gray-500 text-sm mb-1">{mode === "customer" ? "Please wait to be called" : "Task is in the queue"}</p>
                <p className="text-gray-300 text-xs mb-6">Screen resets in 10 seconds</p>
                <button
                  onClick={resetForm}
                  className="bg-blue-600 text-white font-semibold py-2 px-8 rounded-lg hover:bg-blue-700 transition"
                >
                  Done
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* RIGHT PANEL: Queue Display + Admin Controls */}
        <div
          className={`${activeTab === "queue" ? "flex w-full" : "hidden"} bg-gray-50 flex-col overflow-hidden`}
        >

          {/* Now Serving */}
          <div className="bg-white px-4 sm:px-6 py-3 sm:py-4 shadow-sm border-b border-gray-200 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
              {mode === "customer" ? "Now Serving" : "In Progress"}
            </p>

            {serving.length === 0 ? (
              <p className="text-gray-400 text-base py-2 text-center">{mode === "customer" ? "No customers being served" : "No tasks in progress"}</p>
            ) : serving.length === 1 ? (
              <div className="text-center">
                <div className="text-5xl sm:text-7xl font-extrabold text-green-600 leading-none">
                  #{displayNumber(serving[0])}
                </div>
                <div className="text-xl font-medium text-gray-700 mt-1">{serving[0].name}</div>
                {mode === "task" && serving[0].priority && (
                  <div className={`text-xs font-bold px-3 py-1 rounded-full mt-1 inline-block ${serving[0].priority === "high" ? "bg-red-100 text-red-600" : serving[0].priority === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-600"}`}>
                    {serving[0].priority === "high" ? "🔴 High Priority" : serving[0].priority === "medium" ? "🟡 Medium Priority" : "🔵 Low Priority"}
                  </div>
                )}
                {serving[0].notes && (
                  <div className="text-sm text-gray-400 mt-1 italic">{serving[0].notes}</div>
                )}
                <button
                  onClick={() => markServedByNumber(serving[0].number)}
                  className="mt-3 bg-green-600 text-white font-semibold py-2 px-8 rounded-lg hover:bg-green-700 active:scale-95 transition"
                >
                  {mode === "customer" ? "✓ Mark as Served" : "✓ Mark Done"}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {serving.map((cust) => (
                  <div key={cust.number} className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 text-center">
                    <div className="text-2xl sm:text-3xl font-extrabold text-green-600">#{displayNumber(cust)}</div>
                    <div className="text-sm font-medium text-gray-700 truncate">{cust.name}</div>
                    {cust.notes && <div className="text-xs text-gray-400 italic truncate">{cust.notes}</div>}
                    <button
                      onClick={() => markServedByNumber(cust.number)}
                      className="mt-2 w-full bg-green-600 text-white text-xs font-semibold py-1.5 rounded hover:bg-green-700 active:scale-95 transition"
                    >
                      {mode === "customer" ? "✓ Served" : "✓ Done"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Waiting list */}
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              {mode === "customer" ? `Up Next — ${waiting.length} waiting` : `Pending — ${sortedWaiting.length} task${sortedWaiting.length !== 1 ? "s" : ""}`}
            </h3>

            {waiting.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-10">
                {mode === "customer" ? "No one else in queue" : "No pending tasks"}
              </p>
            ) : (
              <ul className="space-y-2">
                {sortedWaiting.map((cust) => (
                  <li
                    key={cust.number}
                    className="bg-white rounded-lg px-4 py-3 shadow-sm flex justify-between items-center"
                  >
                    <div>
                      <span className="font-bold text-gray-800">
                        #{displayNumber(cust)}
                      </span>
                      <span className="ml-2 text-gray-600">{cust.name}</span>
                      {mode === "customer" && formatTimeAgo(cust.checkedInAt) && (
                        <span className="ml-2 text-xs text-gray-400">{formatTimeAgo(cust.checkedInAt)}</span>
                      )}
                      {mode === "task" && cust.priority && (
                        <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${cust.priority === "high" ? "bg-red-100 text-red-600" : cust.priority === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-600"}`}>
                          {cust.priority === "high" ? "🔴 High" : cust.priority === "medium" ? "🟡 Med" : "🔵 Low"}
                        </span>
                      )}
                      {cust.notes && (
                        <p className="text-xs text-gray-400 italic mt-0.5">{cust.notes}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => serveNow(cust.number)}
                        className="bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded text-sm hover:bg-green-100 transition font-medium"
                      >
                        {mode === "customer" ? "Serve Now" : "Start Task"}
                      </button>
                      <button
                        onClick={() => removeCustomer(cust.number)}
                        className="bg-red-50 text-red-600 px-3 py-1 rounded text-sm hover:bg-red-100 transition"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0">
            <button
              onClick={clearAll}
              className="w-full text-sm text-red-600 border border-red-300 bg-red-50 px-4 py-2 rounded-lg hover:bg-red-100 hover:border-red-400 active:scale-95 transition font-medium"
            >
              {mode === "customer" ? "🗑 Clear demo queue & reset counter" : "🗑 Clear all tasks & reset counter"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoQueueManager;
